/**
 * Desktop Detection Utility
 * 
 * Detects if the user is on a desktop device (not mobile web).
 * Uses multiple checks to ensure accuracy:
 * - Pointer media query (fine = mouse/trackpad)
 * - Hover capability
 * - Minimum width breakpoint
 * - Excludes iPad "desktop mode"
 */

export function isDesktop(): boolean {
  if (typeof window === "undefined") return false;

  // Check for mobile user agent patterns first
  const ua = navigator.userAgent;
  const isMobileUA = /iPhone|iPod|Android|Mobile|BlackBerry|Windows Phone|webOS|Opera Mini/i.test(ua);
  
  // Exclude iPad specifically (it can report as desktop in some contexts)
  const isIPad = /iPad/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  
  if (isMobileUA || isIPad) {
    return false;
  }

  // Check pointer media query (fine = mouse/trackpad, coarse = touch)
  const hasFinePointer = window.matchMedia("(pointer: fine)").matches;
  
  // Check hover capability (desktop can hover, mobile cannot)
  const canHover = window.matchMedia("(hover: hover)").matches;
  
  // Check minimum width (desktop typically >= 1024px)
  const hasMinWidth = window.innerWidth >= 1024;
  
  // Desktop if: fine pointer AND can hover AND has minimum width
  return hasFinePointer && canHover && hasMinWidth;
}

/**
 * Check if device is iOS
 */
export function isIOS(): boolean {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Check if device is Android
 */
export function isAndroidDevice(): boolean {
  if (typeof window === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}
